import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, uploadClubDocument } from "@/lib/api";
import { toast } from "sonner";
import { PageHero, Card, C, Loader } from "@/components/district/kit";
import { X, FileArrowUp, Plus, CircleNotch, SealCheck, FloppyDisk, PaperPlaneTilt, Trash, LockSimple, ArrowLeft } from "@phosphor-icons/react";

// Typeahead over the club's membership repository.
function MemberPicker({ members, onPick, testid, compact }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? members.filter((m) => (m.full_name || "").toLowerCase().includes(s)) : members;
    return list.slice(0, 8);
  }, [q, members]);
  return (
    <div className={`relative ${compact ? "w-full" : "w-full sm:w-[280px]"}`}>
      <input data-testid={testid} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 160)} placeholder="Type a name…"
        className="w-full rounded-xl border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
      {open && matches.length > 0 && (
        <div className="absolute z-40 mt-1 max-h-56 w-full overflow-auto rounded-xl border bg-white shadow-lg" style={{ borderColor: C.line }}>
          {matches.map((m) => (
            <button key={m.member_id} data-testid={`${testid}-opt`} onMouseDown={() => { onPick(m); setQ(""); setOpen(false); }}
              className="block w-full px-3 py-2 text-left text-[14px] hover:bg-[#F5F3FF]" style={{ color: C.ink }}>{m.full_name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function ActionBar({ dirty, saving, onDraft, onSubmit }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t bg-white/95 px-4 py-3 backdrop-blur md:pl-[272px]" style={{ borderColor: C.line }} data-testid="constitute-actionbar">
      <div className="mx-auto flex max-w-[1080px] items-center justify-end gap-3">
        {dirty && <span className="mr-auto text-[13px] font-medium" style={{ color: C.sub }}>Unsaved changes</span>}
        <button data-testid="save-draft" onClick={onDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-semibold" style={{ color: C.indigoText, background: C.indigoSoft }}>
          {saving ? <CircleNotch size={15} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />} Save draft
        </button>
        <button data-testid="submit-roster" onClick={onSubmit} disabled={saving} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-semibold text-white" style={{ background: C.indigo }}>
          {saving ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="bold" />} Submit
        </button>
      </div>
    </div>
  );
}

/* ---------------- Board editor (roster; President/Secretary locked from district) ---------------- */
function BoardSection({ item, members, canEdit, onChange, uploadPdf }) {
  const [busy, setBusy] = useState(false);
  const positions = item.positions;
  const set = (next) => onChange(item.item_id, next);
  const assign = (i, m) => set(positions.map((p, idx) => idx === i ? { ...p, member_id: m.member_id, member_name: m.full_name } : p));
  const clear = (i) => set(positions.map((p, idx) => idx === i ? { ...p, member_id: null, member_name: null } : p));
  const addRole = () => set([...positions, { title: "", member_id: null, _custom: true }]);
  const setTitle = (i, t) => set(positions.map((p, idx) => idx === i ? { ...p, title: t } : p));
  const removeRow = (i) => set(positions.filter((_, idx) => idx !== i));
  const filled = positions.filter((p) => p.member_id).length;
  const cell = (p, i) => (
    <div key={i} className="flex flex-col gap-1.5 rounded-lg border p-2" style={{ borderColor: C.line }} data-testid={`position-${item.item_id}-${i}`}>
      <div className="min-w-0">
        {p._custom && !p.member_id ? (
          <input data-testid={`title-input-${item.item_id}-${i}`} value={p.title} onChange={(e) => setTitle(i, e.target.value)} placeholder="Role / position name"
            className="w-full rounded-md border px-2 py-1 text-[13px] font-medium outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
        ) : (
          <span className="block truncate text-[13px] font-semibold" style={{ color: C.ink }} title={p.title || "Untitled role"}>{p.title || "Untitled role"}</span>
        )}
      </div>
      {p.member_id ? (
        <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-semibold" style={{ background: C.indigoSoft, color: C.indigoText }} data-testid={`assigned-${item.item_id}-${i}`}>
          {p.locked ? <LockSimple size={13} weight="fill" /> : <SealCheck size={14} weight="fill" />}
          <span className="min-w-0 flex-1 truncate">{p.member_name}</span>
          {p.locked ? <span className="shrink-0 text-[10px] font-medium opacity-70">from district</span>
            : canEdit && <button data-testid={`clear-${item.item_id}-${i}`} onClick={() => clear(i)} className="shrink-0"><X size={12} weight="bold" /></button>}
        </span>
      ) : canEdit ? (
        <div className="flex items-center gap-1.5">
          <MemberPicker members={members} onPick={(m) => assign(i, m)} testid={`picker-${item.item_id}-${i}`} compact />
          {p._custom && <button onClick={() => removeRow(i)} data-testid={`remove-row-${item.item_id}-${i}`} className="shrink-0 text-[11px] font-semibold" style={{ color: "#E5484D" }}>Remove</button>}
        </div>
      ) : <span className="text-[12px]" style={{ color: C.disabled }}>Unassigned</span>}
    </div>
  );

  // Group the board into four columns:
  //  1) office bearers (President/Secretary/Treasurer families)
  //  2-3) Directors + all remaining roles (Chairs, Editor, Sergeant-at-Arms, custom…)
  //  4) Advisor / Mentor only
  const isAdvisor = (t) => /advisor|mentor/i.test(t || "");
  const isOfficer = (t) => /president|secretary|treasurer/i.test(t || "");
  const indexed = positions.map((p, i) => ({ p, i }));
  const col1 = indexed.filter(({ p }) => isOfficer(p.title));
  const col4 = indexed.filter(({ p }) => isAdvisor(p.title));
  const middle = indexed.filter(({ p }) => !isOfficer(p.title) && !isAdvisor(p.title));
  const half = Math.ceil(middle.length / 2);
  const cols = [col1, middle.slice(0, half), middle.slice(half), col4];
  return (
    <Card data-testid={`section-${item.item_id}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <h3 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>{item.name}</h3>
          <span className="rounded-full px-2.5 py-0.5 text-[13px] font-semibold" style={{ background: filled ? "#E4F7EF" : "#EEF1F6", color: filled ? "#0F8A5F" : C.disabled }}>{filled}/{positions.length} assigned</span>
        </div>
        {canEdit && (
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[14px] font-semibold" style={{ color: C.indigoText }} data-testid={`upload-pdf-${item.item_id}`}>
            {busy ? <CircleNotch size={15} className="animate-spin" /> : <FileArrowUp size={16} weight="bold" />} Upload PDF instead
            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={async (e) => { if (e.target.files[0]) { setBusy(true); await uploadPdf(item, e.target.files[0]); setBusy(false); } }} />
          </label>
        )}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {cols.map((colItems, ci) => (
          <div key={ci} className="flex flex-col gap-2" data-testid={`board-col-${ci}`}>
            {colItems.map(({ p, i }) => cell(p, i))}
          </div>
        ))}
      </div>
      {canEdit && <button data-testid={`add-role-${item.item_id}`} onClick={addRole} className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold" style={{ color: C.indigoText }}><Plus size={15} weight="bold" /> Add a role</button>}
    </Card>
  );
}

function BoardEditor({ clubId }) {
  const [con, setCon] = useState(null);
  const [members, setMembers] = useState([]);
  const [sections, setSections] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const [c, m] = await Promise.all([api.get(`/clubs/${clubId}/constitution`), api.get(`/clubs/${clubId}/members/manage`).catch(() => ({ data: { members: [] } }))]);
    setCon(c.data); setSections(c.data.board); setMembers(m.data.members || []); setDirty(false);
  };
  useEffect(() => { load(); }, [clubId]); // eslint-disable-line
  if (!con) return <Loader label="Loading…" />;
  const onChange = (id, next) => { setSections(sections.map((s) => s.item_id === id ? { ...s, positions: next } : s)); setDirty(true); };
  const uploadPdf = async (item, file) => { try { await uploadClubDocument(clubId, item.item_id, file); toast.success("PDF uploaded"); } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } };
  const saveAll = async (status) => {
    setSaving(true);
    try {
      for (const s of sections) await api.put(`/clubs/${clubId}/roster/${s.item_id}`, { status, positions: s.positions.filter((p) => !p.locked).map((p) => ({ title: p.title, member_id: p.member_id })) });
      toast.success(status === "submitted" ? "Submitted" : "Draft saved"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };
  return (
    <>
      {!con.can_edit && <div className="rounded-xl px-4 py-3 text-[15px] font-medium" style={{ background: "#FEF3E2", color: "#B5730A" }}>Read-only — only the President or Secretary can assign roles.</div>}
      {sections.length === 0 ? <Card><p className="text-[15px]" style={{ color: C.sub }}>The district hasn't set up the board for the year yet.</p></Card>
        : sections.map((it) => <BoardSection key={it.item_id} item={it} members={members} canEdit={con.can_edit} onChange={onChange} uploadPdf={uploadPdf} />)}
      {con.can_edit && sections.length > 0 && <ActionBar dirty={dirty} saving={saving} onDraft={() => saveAll("draft")} onSubmit={() => saveAll("submitted")} />}
    </>
  );
}

/* ---------------- Committee editor (rich 3-column grid) ---------------- */
const COMMITTEE_TINTS = [
  { accent: "#7C4DFF", bg: "#F6F3FF", field: "#EEE9FF" }, // indigo
  { accent: "#0EA5A0", bg: "#ECFBFA", field: "#D8F5F3" }, // turquoise
  { accent: "#E08600", bg: "#FFF7EC", field: "#FDEFD6" }, // amber
  { accent: "#E5484D", bg: "#FEF2F2", field: "#FCE3E3" }, // red
  { accent: "#0F8A5F", bg: "#EDFBF4", field: "#D7F4E6" }, // green
  { accent: "#2563EB", bg: "#EEF3FF", field: "#DDE8FF" }, // blue
  { accent: "#B0468E", bg: "#FCF0F8", field: "#F8DDF0" }, // magenta
];

function CommitteeBox({ committee, ci, members, roles, canEdit, onChange, onRemove }) {
  const tint = COMMITTEE_TINTS[ci % COMMITTEE_TINTS.length];
  const set = (next) => onChange(ci, next);
  const setName = (name) => set({ ...committee, name });
  const addMember = () => set({ ...committee, members: [...committee.members, { role: "Member", member_id: null }] });
  const setRole = (mi, role) => set({ ...committee, members: committee.members.map((m, idx) => idx === mi ? { ...m, role } : m) });
  const assign = (mi, mem) => set({ ...committee, members: committee.members.map((m, idx) => idx === mi ? { ...m, member_id: mem.member_id, member_name: mem.full_name } : m) });
  const removeMember = (mi) => set({ ...committee, members: committee.members.filter((_, idx) => idx !== mi) });
  return (
    <div className="flex flex-col rounded-2xl border p-4 shadow-[0_1px_2px_rgba(84,86,90,0.06)]" style={{ borderColor: C.line, borderTop: `4px solid ${tint.accent}`, background: tint.bg }} data-testid={`committee-${ci}`}>
      <div className="mb-3 flex items-center gap-2">
        {canEdit ? (
          <input data-testid={`committee-name-${ci}`} value={committee.name} onChange={(e) => setName(e.target.value)} placeholder="Committee name"
            className="min-w-0 flex-1 rounded-lg border px-2.5 py-1.5 text-[15px] font-bold outline-none" style={{ borderColor: "transparent", color: tint.accent, background: tint.field }} />
        ) : <h3 className="flex-1 text-[15px] font-bold" style={{ color: tint.accent }}>{committee.name}</h3>}
        {canEdit && <button data-testid={`remove-committee-${ci}`} onClick={() => onRemove(ci)} className="shrink-0" style={{ color: "#E5484D" }}><Trash size={16} weight="bold" /></button>}
      </div>
      <div className="space-y-2">
        {committee.members.map((m, mi) => (
          <div key={mi} className="flex items-center gap-1.5" data-testid={`committee-${ci}-member-${mi}`}>
            {canEdit ? (
              <select data-testid={`committee-${ci}-role-${mi}`} value={m.role} onChange={(e) => setRole(mi, e.target.value)}
                className="w-[92px] shrink-0 rounded-lg border px-1.5 py-1.5 text-[12px] font-semibold outline-none" style={{ borderColor: C.line, color: C.indigoText }}>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            ) : <span className="w-[92px] shrink-0 text-[12px] font-bold" style={{ color: C.indigoText }}>{m.role}</span>}
            {m.member_id ? (
              <span className="flex min-w-0 flex-1 items-center justify-between gap-1 rounded-lg px-2 py-1.5 text-[13px] font-semibold" style={{ background: C.indigoSoft, color: C.ink }}>
                <span className="truncate">{m.member_name}</span>
                {canEdit && <button onClick={() => assign(mi, { member_id: null, full_name: null })} data-testid={`committee-${ci}-clear-${mi}`}><X size={12} weight="bold" /></button>}
              </span>
            ) : canEdit ? <div className="min-w-0 flex-1"><MemberPicker members={members} onPick={(mem) => assign(mi, mem)} testid={`committee-${ci}-picker-${mi}`} compact /></div>
              : <span className="flex-1 text-[13px]" style={{ color: C.disabled }}>—</span>}
            {canEdit && <button onClick={() => removeMember(mi)} data-testid={`committee-${ci}-remove-${mi}`} className="shrink-0 text-[12px]" style={{ color: "#E5484D" }}><X size={14} weight="bold" /></button>}
          </div>
        ))}
      </div>
      {canEdit && <button data-testid={`committee-${ci}-add-member`} onClick={addMember} className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold" style={{ color: C.indigoText }}><Plus size={14} weight="bold" /> Add member</button>}
    </div>
  );
}

function CommitteeEditor({ clubId }) {
  const [data, setData] = useState(null);
  const [committees, setCommittees] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = async () => { const r = await api.get(`/clubs/${clubId}/committees`); setData(r.data); setCommittees(r.data.committees); setDirty(false); };
  useEffect(() => { load(); }, [clubId]); // eslint-disable-line
  if (!data) return <Loader label="Loading committees…" />;
  const change = (ci, next) => { setCommittees(committees.map((c, idx) => idx === ci ? next : c)); setDirty(true); };
  const remove = (ci) => { setCommittees(committees.filter((_, idx) => idx !== ci)); setDirty(true); };
  const addCommittee = () => { setCommittees([...committees, { name: "", members: [{ role: "Chair", member_id: null }, { role: "Advisor", member_id: null }, { role: "Member", member_id: null }, { role: "Member", member_id: null }, { role: "Member", member_id: null }, { role: "Member", member_id: null }] }]); setDirty(true); };
  const save = async (status) => {
    setSaving(true);
    try {
      await api.put(`/clubs/${clubId}/committees`, { status, committees: committees.map((c) => ({ name: c.name, members: c.members.map((m) => ({ role: m.role, member_id: m.member_id })) })) });
      toast.success(status === "submitted" ? "Submitted" : "Draft saved"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };
  return (
    <>
      {!data.can_edit && <div className="rounded-xl px-4 py-3 text-[15px] font-medium" style={{ background: "#FEF3E2", color: "#B5730A" }}>Read-only — only the President or Secretary can build committees.</div>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {committees.map((c, ci) => <CommitteeBox key={ci} committee={c} ci={ci} members={data.members} roles={data.roles} canEdit={data.can_edit} onChange={change} onRemove={remove} />)}
        {data.can_edit && (
          <button data-testid="add-committee" onClick={addCommittee} className="flex min-h-[140px] items-center justify-center rounded-2xl border-2 border-dashed text-[15px] font-semibold" style={{ borderColor: C.line, color: C.indigoText }}>
            <Plus size={18} weight="bold" className="mr-1.5" /> Add committee
          </button>
        )}
      </div>
      {data.can_edit && <ActionBar dirty={dirty} saving={saving} onDraft={() => save("draft")} onSubmit={() => save("submitted")} />}
    </>
  );
}

export default function ClubConstitute({ mode = "board" }) {
  const params = useParams();
  const navigate = useNavigate();
  const [clubId, setClubId] = useState(null);
  const [none, setNone] = useState(false);
  useEffect(() => {
    if (params.clubId) { setClubId(params.clubId); return; }
    api.get("/me/home").then((r) => { if (r.data.club_id) setClubId(r.data.club_id); else setNone(true); });
  }, [params.clubId]);
  if (none) return <p className="text-[16px]" style={{ color: C.sub }}>This is available to club officers.</p>;
  if (!clubId) return <Loader label="Loading…" />;
  const isBoard = mode === "board";
  const backTo = params.clubId ? `/club/${params.clubId}` : "/";
  return (
    <div className="mx-auto w-full max-w-[1080px] space-y-6 pb-24" data-testid="club-constitute-page">
      <button data-testid="back-to-club-dashboard" onClick={() => navigate(backTo)}
        className="inline-flex items-center gap-1.5 text-[14px] font-semibold" style={{ color: C.indigoText }}>
        <ArrowLeft size={16} weight="bold" /> Back to club dashboard
      </button>
      <PageHero title={isBoard ? "Board of Directors" : "Committee appointments"}
        subtitle={isBoard ? "Assign members to each board position, or upload a signed PDF." : "Build each committee — add roles, appoint members, add or remove committees."} />
      {isBoard ? <BoardEditor clubId={clubId} /> : <CommitteeEditor clubId={clubId} />}
    </div>
  );
}
